import { Formik, FormikProps } from "formik";
import { useCallback, useEffect } from "react";
import { IMemberFormValues, memberValidationSchema } from "../../../../common/validations/members-access.validations";
import { useEntity } from "../../../../hooks/use-entity.hook";
import { Flaggers } from "../../../../common/store/flaggers/flaggers.enum";
import { FlaggersMethods } from "../../../../common/store/flaggers/flaggers.methods";

interface MembersFormHandlers {
  handleAddRole: (userEmail: string, roleToAdd: string) => void;
  handleRemoveRole: (userEmail: string, roleToRemove: string) => void;
  handleResetPasswordClick: () => void;
}

interface MembersFormChildrenProps extends FormikProps<IMemberFormValues>, MembersFormHandlers {}

interface MembersFormProps {
  initialValues: IMemberFormValues;
  onFormStateChange?: (values: IMemberFormValues, isValid: boolean) => void;
  children: (props: MembersFormChildrenProps) => React.ReactNode;
}

interface FormContentProps {
  formikProps: FormikProps<IMemberFormValues>;
  onFormStateChange?: (values: IMemberFormValues, isValid: boolean) => void;
  children: (props: MembersFormChildrenProps) => React.ReactNode;
}

const FormContent = ({ formikProps, onFormStateChange, children }: FormContentProps) => {
  const {
    setEntityData: setResetPasswordModalFlagger,
  } = useEntity<{ show?: boolean } | null>({
    entityType: Flaggers.ResetPasswordModalFlagger,
  });
  const { values, isValid, setFieldValue } = formikProps;

  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(values, isValid);
    }
  }, [values, isValid, onFormStateChange]);

  const handleAddRole = useCallback((userEmail: string, roleToAdd: string) => {
    const memberIndex = values.members.findIndex((m) => m.email === userEmail);

    if (memberIndex !== -1) {
      const member = values.members[memberIndex];
      if (!member.role.includes(roleToAdd)) {
        const updatedRoles = [...member.role, roleToAdd];
        setFieldValue(`members.${memberIndex}.role`, updatedRoles);
      }
    }
  }, [values.members, setFieldValue]);

  const handleRemoveRole = useCallback((userEmail: string, roleToRemove: string) => {
    const memberIndex = values.members.findIndex((m) => m.email === userEmail);

    if (memberIndex !== -1) {
      const member = values.members[memberIndex];
      const updatedRoles = member.role.filter((role) => role !== roleToRemove);
      setFieldValue(`members.${memberIndex}.role`, updatedRoles);
    }
  }, [values.members, setFieldValue]);

  const handleResetPasswordClick = () => {
    setResetPasswordModalFlagger({ show: true }, FlaggersMethods.UpdateFlagger);
  };

  return <>{children({ ...formikProps, handleAddRole, handleRemoveRole, handleResetPasswordClick })}</>;
};

const MembersForm = ({
  initialValues,
  onFormStateChange,
  children,
}: MembersFormProps) => {
  const handleSubmit = (values: IMemberFormValues) => {};

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={memberValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validateOnMount={true}
    >
      {(formikProps) => (
        <FormContent
          formikProps={formikProps}
          onFormStateChange={onFormStateChange}
          children={children}
        />
      )}
    </Formik>
  );
};

export default MembersForm;

